import isNil from 'lodash/isNil';

import { DAM_PRECISION, PIPE_VOLUME_PER_TICK_PRECISION } from 'constants/samplePoint';
import { AssetTypeCode } from 'types/models/asset-type';
import SamplePoint, { LevelDisplayUnit, SamplePointConfig, SensorTagId } from 'types/models/samplePoint';
import convertImperialToMetric from 'utils/convert-Imperial-to-metric';
import convertUnit from 'utils/convert-unit';
import { UnitType } from 'utils/get-unit-by-country';
import { isTroughDirect } from 'utils/SamplePoints/trough';
import { convertToLitres } from 'utils/unit-conversions';

export function convertImperialConfigToMetric(
  nextConfig: SamplePointConfig | undefined,
  samplePoint: SamplePoint,
  country: string
): SamplePointConfig {
  const prevConfig: SamplePointConfig | undefined = samplePoint.config;

  if (!nextConfig) {
    return prevConfig;
  }

  let maxValue: number;
  let totalCapacity: number | undefined;
  let updatedConfig: SamplePointConfig = {};
  let maxDepth: number;
  let maxVolume: number;

  switch (samplePoint.assetTypeId) {
    case AssetTypeCode.WATER_TANK:
    case AssetTypeCode.FUEL_TANK:
    case AssetTypeCode.LIQUID_FERTILISER_TANK:
      maxValue = convertImperialToMetric(
        UnitType.LEVEL_SM,
        country,
        nextConfig.maxValue
      ) ?? 0;
      totalCapacity = convertImperialToMetric(
        UnitType.VOLUME_SM,
        country,
        nextConfig.totalCapacity
      );
      updatedConfig = {
        ...nextConfig,
        maxValue,
        totalCapacity
      };
      if (nextConfig.tankLength) {
        updatedConfig.tankLength = convertImperialToMetric(
          UnitType.LEVEL_SM,
          country,
          nextConfig.tankLength
        );
      }
      if (nextConfig.tankWidth) {
        updatedConfig.tankWidth = convertImperialToMetric(
          UnitType.LEVEL_SM,
          country,
          nextConfig.tankWidth
        );
      }
      break;
    case AssetTypeCode.TROUGH: {
      maxValue = convertImperialToMetric(
        UnitType.LEVEL_SM,
        country,
        nextConfig.maxValue
      ) ?? 0;
      updatedConfig = {
        ...nextConfig,
        maxValue
      };
      if (isTroughDirect(samplePoint) && nextConfig.totalCapacity) {
        updatedConfig.totalCapacity = convertImperialToMetric(
          UnitType.VOLUME_SM,
          country,
          nextConfig.totalCapacity
        );
      }
      break;
    }
    case AssetTypeCode.BORE: {
      maxDepth = convertImperialToMetric(
        UnitType.LEVEL_MD,
        country,
        nextConfig.maxDepth
      ) ?? 0;
      const totalBoreDepth = convertImperialToMetric(
        UnitType.LEVEL_MD,
        country,
        nextConfig.totalBoreDepth
      ) ?? 0;
      updatedConfig = {
        ...nextConfig,
        maxDepth,
        totalBoreDepth
      };
      break;
    }
    case AssetTypeCode.PIPE: {
      const isGasPipe = samplePoint.sensorTags.id === SensorTagId.GAS_FLOW_METER;
      const tickConverterLocale = nextConfig.tickConverter;
      const tickConverterMetric = convertImperialToMetric(
        isGasPipe ? UnitType.VOLUME_GAS : UnitType.VOLUME_SM,
        country,
        tickConverterLocale,
        PIPE_VOLUME_PER_TICK_PRECISION
      );
      updatedConfig = {
        ...nextConfig,
        tickConverter: tickConverterMetric
      };
      break;
    }
    case AssetTypeCode.LINE_PRESSURE:
      maxValue = convertImperialToMetric(
        UnitType.PRESSURE_LG,
        country,
        nextConfig.maxValue
      ) ?? 0;
      updatedConfig = {
        ...nextConfig,
        maxValue
      };
      break;
    case AssetTypeCode.DAM:
      if (isNil(nextConfig.maxDepth) || !nextConfig.levelDisplayUnit) {
        return prevConfig;
      } else {
        let unitType = UnitType.LEVEL_MD;
        if (
          [LevelDisplayUnit.CM, LevelDisplayUnit.IN].includes(
            nextConfig.levelDisplayUnit
          )
        ) {
          unitType = UnitType.LEVEL_SM;
        }
        maxDepth = convertUnit(
          { value: nextConfig.maxDepth, unit: { symbol: nextConfig.levelDisplayUnit } },
          { symbol: LevelDisplayUnit.CM },
          DAM_PRECISION
        );
        maxVolume =
          convertToLitres(
            Number(nextConfig.maxVolume ?? 0),
            nextConfig.volumeDisplayUnit
          ) ?? 0;
        updatedConfig = {
          ...prevConfig, // Preserve enabledVolumeMapping
          ...nextConfig, // Update enabledVolumeMapping if presents
          maxDepth,
          maxVolume,
          volumeDisplayUnit: nextConfig.volumeDisplayUnit
        };
      }
      break;
    case AssetTypeCode.CAMERA:
      updatedConfig = nextConfig || samplePoint.config;
      break;
    default:
      updatedConfig = {
        ...prevConfig,
        ...nextConfig
      };
  }

  // Currently, wind is the only sensor that supports offset not in length units
  if (samplePoint.assetTypeId === AssetTypeCode.WIND) {
    updatedConfig.offset = nextConfig.offset;
  }
  else if (!isNil(nextConfig.offset)) {
    updatedConfig.offset = convertImperialToMetric(
      UnitType.LEVEL_SM,
      country,
      Number(nextConfig.offset)
    );
  } else {
    delete updatedConfig.offset;
  }

  return updatedConfig;
}